import type { TopLevelNavColumnItem } from '../types';
import type { KeyboardEvent, MouseEvent, TouchEvent } from 'react';

import { memo, useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';

import { TopLevelNavigationColumn } from './TopLevelNavigationColumn';
import { S } from '../TopLevelNavigation.styled';
import { getCategoryLabelId } from '../utils';

const SWIPE_THRESHOLD = 40;

type TopLevelNavigationPanelProps = {
  panelId: string;
  open: boolean;
  activeCategoryIndex: number | null;
  categoryLabel: string;
  columns: TopLevelNavColumnItem[];
  closeNavigation: (returnFocusToTrigger?: boolean) => void;
  activateLinkWithSpace: (event: KeyboardEvent<HTMLAnchorElement>) => void;
};

const TopLevelNavigationPanelComponent = ({
  panelId,
  open,
  activeCategoryIndex,
  categoryLabel,
  columns,
  closeNavigation,
  activateLinkWithSpace,
}: TopLevelNavigationPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const swipedRecentlyRef = useRef(false);

  const handlePanelKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Escape is handled by the window-level listener in TopLevelNavigation.
      if (event.key === 'Tab') {
        const focusableElements = panelRef.current?.querySelectorAll<HTMLElement>(
          `a[href], button:not([disabled])`
        );

        if (!focusableElements || focusableElements.length === 0) {
          return;
        }

        // Tabbing out of the panel in either direction closes it and returns focus to the trigger.
        const edgeElement = event.shiftKey
          ? focusableElements[0]
          : focusableElements[focusableElements.length - 1];

        if (edgeElement.contains(document.activeElement)) {
          event.preventDefault();
          closeNavigation(true);
        }
      }
    },
    [closeNavigation]
  );

  const handlePanelTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    if (event.touches.length === 1) {
      touchStartYRef.current = event.touches[0].clientY;
      touchStartXRef.current = event.touches[0].clientX;
    }
  }, []);

  const handlePanelTouchEnd = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (touchStartYRef.current === null || touchStartXRef.current === null) {
        return;
      }

      const touchEndY = event.changedTouches[0].clientY;
      const touchEndX = event.changedTouches[0].clientX;
      const deltaY = touchStartYRef.current - touchEndY;
      const deltaX = Math.abs(touchEndX - touchStartXRef.current);

      touchStartYRef.current = null;
      touchStartXRef.current = null;

      if (deltaY >= SWIPE_THRESHOLD && deltaY > deltaX) {
        swipedRecentlyRef.current = true;
        closeNavigation();

        setTimeout(() => {
          swipedRecentlyRef.current = false;
        }, 300);
      }
    },
    [closeNavigation]
  );

  const handlePanelTouchCancel = useCallback(() => {
    touchStartYRef.current = null;
    touchStartXRef.current = null;
  }, []);

  const handlePanelClickCapture = useCallback((event: MouseEvent) => {
    if (swipedRecentlyRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return (
    <>
      <S.Backdrop
        $open={open}
        data-testid="top-level-navigation-backdrop"
        role="presentation"
        onClick={() => closeNavigation()}
        onTouchStart={() => closeNavigation()}
      />
      <S.Panel
        ref={panelRef}
        data-testid="top-level-navigation-panel"
        $open={open}
        id={panelId}
        role="region"
        aria-labelledby={
          activeCategoryIndex === null ? undefined : getCategoryLabelId(activeCategoryIndex)
        }
        aria-hidden={!open}
        tabIndex={-1}
        onClickCapture={handlePanelClickCapture}
        onKeyDown={handlePanelKeyDown}
        onTouchCancel={handlePanelTouchCancel}
        onTouchEnd={handlePanelTouchEnd}
        onTouchStart={handlePanelTouchStart}
      >
        <S.Columns>
          {columns.map((column, columnIndex) => (
            <TopLevelNavigationColumn
              key={column.id}
              column={column}
              columnIndex={columnIndex}
              activeCategoryIndex={activeCategoryIndex}
              categoryLabel={categoryLabel}
              activateLinkWithSpace={activateLinkWithSpace}
            />
          ))}
        </S.Columns>
        <S.SwipeIndicator
          data-testid="top-level-navigation-swipe-indicator"
          aria-hidden="true"
          onClick={() => closeNavigation()}
        >
          <S.SwipeChevron />
          <S.SwipeBar />
        </S.SwipeIndicator>
      </S.Panel>
    </>
  );
};

export const TopLevelNavigationPanel = memo(TopLevelNavigationPanelComponent, isEqual);
